import { Injectable } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNgToastSeverity } from '@/core/types/angular-primeng-api';

/**
 * An enum of predefined toast messages.
 */

const predefinedToasts = {
    SAVE_SUCCESS: (ctx: ToastService): void => ctx.success('standard_success', 'standard_success_saved'),
    STANDARD_ERROR: (ctx: ToastService): void => ctx.error('standard_error', 'standard_error_sorry'),
    UNEXPECTED_ERROR: (ctx: ToastService): void => ctx.error('standard_error', 'standard_unexpected_error'),
} as const;

export const StdToasts = Object.freeze(Object.fromEntries(Object.keys(predefinedToasts).map((k) => [k, k]))) as Record<
    keyof typeof predefinedToasts,
    keyof typeof predefinedToasts
>;

/**
 * @class ToastService
 * This is a simple API wrapper around the PrimeNG MessageService.
 * It includes translation capabilities.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
    constructor(
        private readonly messageService: MessageService,
        private readonly translate: TranslateService,
    ) {}

    private buildMessageObject(
        severity: PrimeNgToastSeverity | 'error',
        summary: string,
        detail?: string,
        key?: string,
        life?: number,
    ): ToastMessageOptions {
        const msgObj: ToastMessageOptions = {
            severity: severity,
            summary: this.translate.instant(summary),
        };
        if (detail) {
            msgObj.detail = this.translate.instant(detail);
        }
        if (key) {
            msgObj.key = key;
        }
        if (life) {
            msgObj.life = life;
        }
        return msgObj;
    }

    public displayToastWithKey(severity: PrimeNgToastSeverity, key: string, summary: string, detail?: string): void {
        this.messageService.add(this.buildMessageObject(severity, summary, detail, key));
    }

    // error is not a valid severity, but used in our code. It exists in the documentation, but not in the type. Needs testing.
    public displayToast(
        severity: PrimeNgToastSeverity | 'error',
        summary: string,
        detail?: string,
        life?: number,
    ): void {
        this.messageService.add(this.buildMessageObject(severity, summary, detail, undefined, life));
    }

    /**
     * Displays a toast message with HTML content in the localized texts.
     *
     * Attention:
     * This method uses a specific key and template. As a result, it is rendered to an own container (p-toast behavior).
     * For this reason the elements will not be stacked with other toasts. Instead, they are displayed overlapping.
     * Use with caution.
     *
     */
    public displayToastWithHtml(
        severity: PrimeNgToastSeverity | 'error',
        summary: string,
        detail?: string,
        life?: number,
    ): void {
        this.messageService.add(this.buildMessageObject(severity, summary, detail, 'withHtml', life));
    }

    public danger(summary: string, detail?: string, life?: number): void {
        this.displayToast('danger', summary, detail, life);
    }

    public success(summary: string, detail?: string, life?: number): void {
        this.displayToast('success', summary, detail, life);
    }

    public info(summary: string, detail?: string, life?: number): void {
        this.displayToast('info', summary, detail, life);
    }

    public warn(summary: string, detail?: string, life?: number): void {
        this.displayToast('warn', summary, detail, life);
    }

    public clearToasts(): void {
        this.messageService.clear();
    }

    public error(summary: string = 'standard_error', detail?: string, life?: number): void {
        this.displayToast('error', summary, detail, life);
    }

    public displayToastNoTranslate(
        severity: PrimeNgToastSeverity | 'error',
        summary: string,
        detail?: string,
        life?: number,
    ): void {
        this.messageService.add({
            severity: severity,
            summary: summary,
            detail: detail ? detail : undefined,
            life: life ? life : undefined,
        });
    }

    public successNoTranslate(summary: string, detail?: string, life?: number): void {
        this.displayToastNoTranslate('success', summary, detail, life);
    }

    public warnNoTranslate(summary: string, detail?: string, life?: number): void {
        this.displayToastNoTranslate('warn', summary, detail, life);
    }

    public errorNoTranslate(summary: string, detail?: string, life?: number): void {
        this.displayToastNoTranslate('error', summary, detail, life);
    }

    /**
     * This method displays a predefined toast message.
     * Often reused toasts can be defined here.
     * @param type The toast definition to display.
     */
    public toast(type: keyof typeof predefinedToasts): void {
        predefinedToasts[type](this);
    }
}
